<template>
  <v-card-text>
    <v-layout row wrap fill-height>
      <v-flex>
        <template v-if="logs">
          <v-flex>
            <div class="pb-2 headline">Filters</div>
            <v-chip v-if="startDateText && endDateText" small>
              Between &nbsp;
              <strong>{{ prettyDate(startDateText) || moment("MMMM Do YYYY")}}</strong>
              &nbsp;
              &mdash;
              &nbsp;
              <strong>{{ prettyDate(endDateText) || moment("MMMM Do YYYY")}}</strong>
            </v-chip>
            <v-chip small v-if="searchTermText">Search logs: &nbsp;<strong>{{searchTermText}}</strong></v-chip>
            <v-chip small v-if="tenantText">Tenant &nbsp;<strong>{{tenantText}}</strong></v-chip>
            <v-chip small v-if="showLogsIfHasErrorText">{{showLogsIfHasErrorText}}</v-chip>
            <v-chip small v-if="showLogsIfNotTestRunText">{{showLogsIfNotTestRunText}}</v-chip>
            <v-chip small v-if="showLogsIfNotSniffText">{{showLogsIfNotSniffText}}</v-chip>
            <v-chip small v-if="executionIdText">With execution id: &nbsp;<strong>{{executionIdText}}</strong></v-chip>
            <v-chip small v-if="isRollback">{{showRollbackLogsText}}</v-chip>

            <div class="headline mt-3">Logs</div>
            <div v-if="logs.length > 0" class="my-3">
              <v-icon color="info" style="line-height: 19px;">error_outline</v-icon>
              <strong>{{ logs.length }} {{ pluralize('log', logs.length) }}</strong> found.
              <br />Logs are grouped by their <b>process id</b> and then sorted by <b>date</b>.
            </div>

            <div v-else class="my-3">
              <v-icon class="mr-1" color="error" style="line-height: 19px;">error_outline</v-icon>
              No results found
            </div>
          </v-flex>

          <LogsGroupByProcessId :logs="logs" />
        </template>
      </v-flex>
    </v-layout>
  </v-card-text>
</template>

<script>
  import LogsGroupByProcessId from './Grouping/LogsGroupByProcessId'
  import { prettyDateFormat } from '@/components/mixins/MomentUtil'
  import moment from 'moment'
  import { pluralize } from '@/helpers/strings'

  export default {
    components: {
      LogsGroupByProcessId,
    },
    props: [
      'processInstanceId',
      'executionId',
      'tenant',
      'startDate',
      'endDate',
      'minScore',
      'searchTerm',
      'showLogsIfHasError',
      'showLogsIfNotTestRun',
      'showLogsIfNotSniff',
      'serviceName',
      'isRollback',
    ],
    data() {
      return {
        logs: null,
        selectedProcessInstanceId: '',
        selectedEndDate: '',
        elasticQuery: '',
        searchTermText: '',
        startDateText: '',
        endDateText: '',
        tenantText: '',
        showLogsIfHasErrorText: '',
        showLogsIfNotTestRunText: '',
        showLogsIfNotSniffText: '',
        executionIdText: '',
        showRollbackLogsText: '',
        defaultMinScore: 0.5,
        defaultStartDate: null,
        defaultEndDate: 'now',
      }
    },
    computed: {
      tenantId() {
        return this.tenant && this.tenant.Id
      },
    },
    methods: {
      pluralize,
      prettyDate(date) {
        return moment(date).format(prettyDateFormat)
      },
      async fetchLogs() {
        this.logs = null
        this.constructElasticSearchQuery()
        await this.getLogsWithConstructedQuery()
      },
      setLoading(value) {
        this.$emit('update:loading', value)
      },
      resetLogs() {
        this.logs = null
      },
      constructElasticSearchQuery() {
        this.startQueryConstruction({
          minScore: this.minScore ? this.minScore : this.defaultMinScore,
        })

        if (this.processInstanceId) {
          this.addQueryProcessInstanceId(this.processInstanceId)
        }

        if (this.executionId) {
          this.addQueryExecutionId(this.executionId)
        }

        if (this.tenantId) {
          this.addQueryTenantId(this.tenantId)
        }

        if (this.serviceName) {
          this.addQueryServiceName(this.serviceName)
        }

        if (this.searchTerm) {
          this.addQueryFuzzySearch(this.searchTerm)
        }

        if (this.showLogsIfNotTestRun) {
          this.addQueryShowOnlyExecutionLogs(this.showLogsIfNotTestRun)
        }

        if (this.showLogsIfNotSniff) {
          this.addQueryShowSniffLogs(this.showLogsIfNotSniff)
        }

        if (this.isRollback != null && this.isRollback != undefined) {
          this.addQueryShowRollback(this.isRollback)
        }
        // defaults to 14 days ago for start date, and now for end date if no date passed
        // this must be the last query we add as it doesn't have an extra comma afer the query.
        // elastic search is very picky about json syntax
        this.addQueryDateRange({
          startDate: this.startDate ? this.startDate : this.defaultStartDate,
          endDate: this.endDate ? this.endDate : this.defaultEndDate,
        })

        this.endQueryConstruction()
      },
      startQueryConstruction({
        minScore,
      } = {}) {
        this.elasticQuery = `
      {
        "size" : "10000",
        "min_score" : ${minScore},
        "sort": [
                  { 
                    "ExecutionDate" : {
                      "order" : "asc"
                    }
                  }
               ],
        "query": {
          "bool": {
            "must": [`
      },
      addQueryFuzzySearch(searchTerm) {
        this.elasticQuery += `
              {
                "query_string": {
                  "query": "${searchTerm}*"
                }
              },`
      },
      addQueryTenantId(tenantId) {
        this.elasticQuery += `
              {
                "match" : {
                  "TenantId": ${tenantId}
                }
              },`
      },
      addQueryProcessInstanceId(processInstanceId) {
        this.elasticQuery += `
              {
                "match" : {
                  "ProcessInstanceId": "${processInstanceId}"
                }
              },`
      },
      addQueryExecutionId(executionId) {
        this.elasticQuery += `
              {
                "match" : {
                  "ExecutionId": "${executionId}"
                }
              },`
      },
      addQueryServiceName(serviceName) {
        this.elasticQuery += `
              {
                "match" : {
                  "ServiceName": "${serviceName}"
                }
              },`
      },
      addQueryShowOnlyExecutionLogs(showLogsIfNotTestRun) {
        this.elasticQuery += `
              {
                "match" : {
                  "IsTestRun": ${!showLogsIfNotTestRun}
                }
              },`
      },
      addQueryShowRollback(isRollback) {
        this.elasticQuery += `
              {
                "match" : {
                  "IsRollback": ${!!isRollback}
                }
              },`
      },
      addQueryShowSniffLogs(showLogsIfNotSniff) {
        this.elasticQuery += `
              {
                "match" : {
                  "IsCheckFileFormat": ${!showLogsIfNotSniff}
                }
              },`
      },
      addQueryDateRange({
        startDate,
        endDate,
      } = {}) {
        // dates must be in format of: 2019-07-18T00:00:00
        // can use 'now' to represent current date
        this.elasticQuery += `
              {
                "range" : {
                "ExecutionDate" : {
                  "format": "yyyy-MM-dd HH:mm:ss||yyyy-MM-dd||epoch_millis||strict_date_optional_time",
                  "gte": "${startDate}", 
                  "lte": "${endDate}" 
                  }
                }
              }`
      },
      endQueryConstruction() {
        this.elasticQuery += `
              ],
            }
          }
        }`
      },
      async getLogsWithConstructedQuery() {
        this.searchTermText = this.searchTerm
        this.startDateText = this.startDate
        this.endDateText = this.endDate
        this.tenantText = this.tenantId && this.tenant.RoleName
        this.showLogsIfHasErrorText = this.showLogsIfHasError && 'Errors only'
        this.showLogsIfNotTestRunText = this.showLogsIfNotTestRun && 'No test runs'
        this.showLogsIfNotSniffText = this.showLogsIfNotSniff && 'No file format checks'
        this.executionIdText = this.executionId
        this.showRollbackLogsText = this.isRollback && 'Rollback logs only'

        this.setLoading(true)
        this.$store.dispatch('servicelogs/queryElasticSearch', this.elasticQuery)
          .then(response => {
            this.logs = response.data.hits.hits
            this.makeEventLevelCounts()
            Object.freeze(this.logs)
            this.setLoading(false)
          })
          .catch(error => {
            this.setLoading(false)
            console.error(error)
            this.resetLogs()
          })
      },
      makeEventLevelCounts() {
        for (var i = 0; i < this.logs.length; i++) {
          this.logs[i]['Counts'] = this.getEventLevelCounts(this.logs[i]._source.EventRecords)
        }
      },
      getEventLevelCounts(eventRecords) {
        var counts = {
          'Info': 0,
          'Warn': 0,
          'Error': 0,
        }
        for (var idx in eventRecords) {
          counts[eventRecords[idx].EventLevel] += 1
          if (eventRecords[idx]['InnerEventRecords'] === null) {
            continue
          }
          var innerEventRecords = eventRecords[idx]['InnerEventRecords']
          for (var innerIdx in innerEventRecords) {
            counts[innerEventRecords[innerIdx].EventLevel] += 1
          }
        }
        return counts
      },
    },
    mounted() {
      this.defaultStartDate = moment().subtract(14, 'days').format('YYYY-MM-DDTHH:mm:ss')
    },
  }
</script>